import React, { useEffect } from 'react'
import * as yup from 'yup'
import { Form, Formik } from 'formik'
import { CircularProgress, Grid, Typography } from '@mui/material'

import {
  PasswordField,
  SelectEvent,
  StyledButton,
  getTokensFromStorage,
  isObjectEmpty,
  useStepper,
} from 'shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { changePassword } from 'api/auth'
import { toast } from 'react-toastify'

const initialValues = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirmation: '',
}

export const validationSchema = yup.object().shape({
  currentPassword: yup.string().required('Your current password is required'),
  newPassword: yup.string().required('New password is required'),
  newPasswordConfirmation: yup
    .string()
    .required('New password confirmation is required')
    .oneOf([yup.ref('newPassword')], 'Passwords should match'),
})

const ChangePasswordStep = ({
  onMount,
  closeModal,
}: {
  onMount: () => void
  closeModal: () => void
}) => {
  const client = useQueryClient()
  const { goToNextStep } = useStepper()

  const { isLoading, mutate } = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      client.invalidateQueries(['user'])
      toast.success('Password updated successfully')
      closeModal()
    },
  })

  const onSubmit = async (values: typeof initialValues) => {
    const tokens = getTokensFromStorage()
    mutate({
      previousPassword: values.currentPassword,
      proposedPassword: values.newPassword,
      accessToken: tokens.access_token,
    })
  }

  useEffect(() => {
    onMount()
  }, [])

  return (
    <Grid>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ dirty, errors, values, setFieldValue }) => {
          return (
            <Form>
              <PasswordField
                name="currentPassword"
                label="Current password"
                value={values.currentPassword}
                placeholder="Enter current password"
                margin="dense"
                type="password"
                onChange={(e: SelectEvent) =>
                  setFieldValue('currentPassword', e.target.value)
                }
              />
              <PasswordField
                name="newPassword"
                label="New password"
                value={values.newPassword}
                placeholder="Enter new password"
                margin="dense"
                type="password"
                onChange={(e: SelectEvent) =>
                  setFieldValue('newPassword', e.target.value)
                }
              />
              <PasswordField
                name="newPasswordConfirmation"
                label="Confirm password"
                value={values.newPasswordConfirmation}
                placeholder="Confirm new password"
                type="password"
                margin="dense"
                onChange={(e: SelectEvent) =>
                  setFieldValue('newPasswordConfirmation', e.target.value)
                }
              />
              <Typography
                marginTop="12px"
                fontWeight="600"
                sx={{
                  cursor: 'pointer',
                  color: (theme) => theme.palette.peach.main,
                }}
                onClick={goToNextStep}
              >
                Forgot password ?
              </Typography>
              <Grid
                mt={2}
                gap={2}
                container
                alignItems="center"
                justifyContent="flex-end"
              >
                <StyledButton variant="outlined" onClick={closeModal}>
                  Cancel
                </StyledButton>
                <StyledButton
                  type="submit"
                  variant="contained"
                  disabled={!dirty || !isObjectEmpty(errors)}
                  startIcon={
                    isLoading ? (
                      <CircularProgress sx={{ color: 'white' }} size="1em" />
                    ) : null
                  }
                >
                  Submit
                </StyledButton>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Grid>
  )
}

export default ChangePasswordStep
